import React, { useState } from 'react';
import { Input, Badge } from '@components';

interface RecipientsInputProps {
  id: string;
  label: string;
  placeholder: string;
  hint: string;
  recipients: string[];
  onRecipientsChange: (recipients: string[]) => void;
  validate?: (value: string) => boolean;
  inputType?: 'email' | 'tel' | 'text';
}

export const RecipientsInput: React.FC<RecipientsInputProps> = ({
  id,
  label,
  placeholder,
  hint,
  recipients,
  onRecipientsChange,
  validate,
  inputType = 'text',
}) => {
  const [input, setInput] = useState('');

  const handleAddRecipients = (text: string) => {
    if (!text.trim()) return;

    const items = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0 && (!validate || validate(item)));

    const newItems = items.filter(item => !recipients.includes(item));
    if (newItems.length > 0) {
      onRecipientsChange([...recipients, ...newItems]);
      setInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddRecipients(input);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    handleAddRecipients(pastedText);
  };

  const handleRemove = (index: number) => {
    const updated = recipients.filter((_, i) => i !== index);
    onRecipientsChange(updated);
  };

  return (
    <div className="mt-6">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        {label}
      </label>
      <div className="space-y-2">
        <Input
          id={id}
          type={inputType}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="w-full"
        />
        {recipients.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {recipients.map((recipient, index) => (
              <Badge
                key={index}
                variant="default"
                className="inline-flex items-center gap-1 px-2 py-1"
              >
                {recipient}
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="ml-1 hover:opacity-70 focus:outline-none"
                >
                  <span className="material-icons text-sm">close</span>
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>
    </div>
  );
};
